/**
 * P1-004 Benefits Dashboard - Outcome Intelligence Layer
 * 
 * The UI that answers: "Did the investment create the value we approved?"
 * 
 * Three-View Dashboard for Outcome Intelligence:
 * 1. Executive View - Did we deliver value? What's at risk? Which investments underperformed?
 * 2. PMO View - Which benefits are drifting? Delayed? Lack ownership?
 * 3. Governance View - Which benefits required approval? Triggered governance events?
 * 
 * Architecture Position: Intent → Decision → Governance → Delivery → OUTCOME (Benefits)
 * 
 * Executive Questions Answered:
 * - What value was expected?
 * - What value was delivered?
 * - Which portfolios generate the most value?
 * - Which programs produce the most value?
 * - Which projects produce the most value?
 * - Which investments underperformed?
 * - Where should investment increase/stop?
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target, AlertTriangle, CheckCircle, Clock, TrendingUp,
  TrendingDown, Users, FileText, BarChart3, PieChart as PieChartIcon,
  RefreshCw, Loader2, ChevronRight, AlertCircle, Eye, Bell,
  Activity, Zap, Award, Scale, Gauge, DollarSign, Briefcase,
  Building2, Layers, ArrowUpRight, ArrowDownRight, Percent,
  Calendar, ChevronDown, Filter, Search, Plus
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ComposedChart
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency, getCurrencySymbol } from '../utils/currencyUtils';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId =
    localStorage.getItem('impersonated_tenant_id') ||
    localStorage.getItem('simpliview_user_tenant_id') ||
    localStorage.getItem('simpliview_tenant_id');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {})
  };
};

// =============================================================================
// FORMATTING HELPERS (now uses tenant currency via hook at component level)
// =============================================================================

const formatCurrencyStatic = (value, symbol = '$') => {
  if (value === undefined || value === null) return '-';
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (absValue >= 1000000) {
    return `${sign}${symbol}${(absValue / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    return `${sign}${symbol}${(absValue / 1000).toFixed(0)}K`;
  }
  return `${sign}${symbol}${absValue.toLocaleString()}`;
};

const formatPercent = (value) => {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(1)}%`;
};

// =============================================================================
// SHARED COMPONENTS
// =============================================================================

const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue', testId, onClick }) => {
  const colorMap = {
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-100' },
    green: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-100' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-100' },
    red: { bg: 'bg-red-50', icon: 'text-red-600', border: 'border-red-100' },
    purple: { bg: 'bg-purple-50', icon: 'text-purple-600', border: 'border-purple-100' },
    slate: { bg: 'bg-slate-50', icon: 'text-slate-600', border: 'border-slate-100' },
    indigo: { bg: 'bg-indigo-50', icon: 'text-indigo-600', border: 'border-indigo-100' }
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <div 
      className={`bg-white rounded-xl border ${c.border} p-5 shadow-sm hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
      data-testid={testId}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-bold text-slate-900">{value}</span>
            {trend !== undefined && (
              <span className={`text-sm font-medium flex items-center gap-0.5 ${
                trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-red-600' : 'text-slate-500'
              }`}>
                {trend > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : trend < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : null}
                {trend > 0 ? '+' : ''}{trend}%
              </span>
            )}
          </div>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl ${c.bg}`}>
          <Icon className={`w-5 h-5 ${c.icon}`} />
        </div>
      </div>
    </div>
  );
};

const AttainmentGauge = ({ value, label, target = 100, size = 'medium' }) => {
  const percentage = Math.min(value || 0, 120);
  const sizeMap = {
    small: { container: 'w-16 h-16', text: 'text-sm' },
    medium: { container: 'w-24 h-24', text: 'text-lg' },
    large: { container: 'w-32 h-32', text: 'text-2xl' }
  };
  const s = sizeMap[size] || sizeMap.medium;
  
  const getColor = (val) => {
    if (val >= 100) return '#10b981'; // Green - exceeded
    if (val >= 80) return '#10b981';  // Green - on track
    if (val >= 60) return '#f59e0b';  // Amber - at risk
    return '#ef4444';                  // Red - below target
  };

  return (
    <div className="flex flex-col items-center">
      <div className={`relative ${s.container}`}>
        <svg className="transform -rotate-90" viewBox="0 0 36 36">
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="3"
          />
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke={getColor(value)}
            strokeWidth="3"
            strokeDasharray={`${Math.min(percentage, 100)}, 100`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`${s.text} font-bold`} style={{ color: getColor(value) }}>
            {(value || 0).toFixed(0)}%
          </span>
        </div>
      </div>
      {label && <p className="text-sm font-medium text-slate-600 mt-2 text-center">{label}</p>}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    on_track: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'On Track', icon: CheckCircle },
    exceeded: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Exceeded', icon: TrendingUp },
    at_risk: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'At Risk', icon: AlertTriangle },
    below_target: { bg: 'bg-red-100', text: 'text-red-700', label: 'Below Target', icon: TrendingDown },
    cancelled: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Cancelled', icon: AlertCircle },
    deferred: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Deferred', icon: Clock }
  };
  
  const config = statusConfig[status] || statusConfig.on_track;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

const LifecycleBadge = ({ lifecycle }) => {
  const lifecycleConfig = {
    proposed: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Proposed' },
    approved: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Approved' },
    tracked: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Tracked' },
    realized: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Realized' },
    closed: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Closed' }
  };
  
  const config = lifecycleConfig[lifecycle] || lifecycleConfig.proposed;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

const CategoryBadge = ({ category }) => {
  const categoryConfig = {
    financial: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: DollarSign },
    operational: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Activity },
    strategic: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Target },
    compliance: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Scale },
    customer: { bg: 'bg-pink-100', text: 'text-pink-700', icon: Users },
    productivity: { bg: 'bg-indigo-100', text: 'text-indigo-700', icon: Zap },
    risk_reduction: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle }
  };
  
  const config = categoryConfig[category] || categoryConfig.financial;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" />
      {category?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
    </span>
  );
};

const BenefitCard = ({ benefit, onView, currencySymbol = '$' }) => {
  const attainment = benefit.attainment_percent || 0;
  const isAtRisk = benefit.status === 'at_risk' || benefit.status === 'below_target';
  
  return (
    <div 
      className={`bg-white rounded-lg border p-4 hover:shadow-md transition-shadow cursor-pointer ${
        isAtRisk ? 'border-l-4 border-l-red-500' : 'border-l-4 border-l-emerald-500'
      }`}
      onClick={() => onView?.(benefit)}
      data-testid={`benefit-${benefit.benefit_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <StatusBadge status={benefit.status} />
            <LifecycleBadge lifecycle={benefit.lifecycle} />
            <CategoryBadge category={benefit.category} />
          </div>
          <h4 className="font-medium text-slate-900 truncate">{benefit.title}</h4>
          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{benefit.description}</p>
        </div>
        <AttainmentGauge value={attainment} size="small" />
      </div>
      
      <div className="mt-4 pt-3 border-t border-slate-100">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-slate-400">Expected</p>
            <p className="text-sm font-semibold text-slate-700">
              {formatCurrencyStatic(benefit.expected_value, currencySymbol)}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Realized</p>
            <p className="text-sm font-semibold text-slate-700">
              {formatCurrencyStatic(benefit.realized_value, currencySymbol)}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Variance</p>
            <p className={`text-sm font-semibold ${
              (benefit.variance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
            }`}>
              {formatCurrencyStatic(benefit.variance || 0, currencySymbol)}
            </p>
          </div>
        </div>
      </div>
      
      {benefit.owner_name && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
          <Users className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600">{benefit.owner_name}</span>
          {benefit.target_date && (
            <>
              <span className="text-slate-300">•</span>
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-600">
                Target: {new Date(benefit.target_date).toLocaleDateString()}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ViewTab = ({ active, label, icon: Icon, count, onClick, testId }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
      active 
        ? 'bg-slate-900 text-white shadow-lg' 
        : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
    }`}
    data-testid={testId || `tab-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Icon className="w-5 h-5" />
    <span>{label}</span>
    {count !== undefined && (
      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
        active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
      }`}>
        {count}
      </span>
    )}
  </button>
);

// =============================================================================
// EXECUTIVE VIEW - Did we deliver value?
// =============================================================================

const ExecutiveView = ({ summary, outcomeScore, atRiskBenefits, byCategory, topProjects, onViewBenefit, currencySymbol = '$' }) => {
  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899', '#6366f1', '#ef4444'];
  
  // Create a currency formatter using the symbol
  const fmt = (val) => formatCurrencyStatic(val, currencySymbol);
  
  // Category pie chart data
  const categoryData = Object.entries(byCategory || {}).map(([key, value], index) => ({
    name: key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value: value.expected_value || 0,
    realized: value.realized_value || 0,
    attainment: value.attainment_percent || 0,
    count: value.count || 0,
    color: COLORS[index % COLORS.length]
  })).filter(d => d.value > 0);
  
  return (
    <div className="space-y-6" data-testid="executive-view">
      {/* Hero Metric - Outcome Achievement Score */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-slate-300 mb-2">Outcome Achievement Score</h2>
            <div className="flex items-baseline gap-4">
              <span className="text-5xl font-bold">{formatPercent(outcomeScore?.outcome_achievement_score)}</span>
              <span className={`text-lg px-3 py-1 rounded-full ${
                outcomeScore?.outcome_achievement_score >= 80 ? 'bg-emerald-500/20 text-emerald-300' :
                outcomeScore?.outcome_achievement_score >= 60 ? 'bg-amber-500/20 text-amber-300' :
                'bg-red-500/20 text-red-300'
              }`}>
                {outcomeScore?.interpretation || 'Calculating...'}
              </span>
            </div>
            <p className="text-slate-400 mt-3">
              {outcomeScore?.total_benefits || 0} benefits tracked across portfolio
            </p>
          </div>
          <div className="text-right">
            <div className="grid grid-cols-3 gap-8">
              <div>
                <p className="text-slate-400 text-sm">Expected Value</p>
                <p className="text-2xl font-bold">{fmt(summary?.total_expected_value)}</p>
              </div>
              <div>
                <p className="text-slate-400 text-sm">Realized Value</p>
                <p className="text-2xl font-bold text-emerald-400">{fmt(summary?.total_realized_value)}</p>
              </div>
              <div>
                <p className="text-slate-400 text-sm">Value Gap</p>
                <p className="text-2xl font-bold text-red-400">
                  {fmt((summary?.total_expected_value || 0) - (summary?.total_realized_value || 0))}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Benefits On Track"
          value={summary?.benefits_on_track || 0}
          subtitle={`${formatPercent(outcomeScore?.benefits_realized_percent)} of total`}
          icon={CheckCircle}
          color="green"
          testId="metric-on-track"
        />
        <MetricCard
          title="Benefits At Risk"
          value={summary?.benefits_at_risk || 0}
          subtitle="Requires intervention"
          icon={AlertTriangle}
          color={summary?.benefits_at_risk > 0 ? 'amber' : 'green'}
          testId="metric-at-risk"
        />
        <MetricCard
          title="Below Target"
          value={summary?.benefits_below_target || 0}
          subtitle="Executive attention needed"
          icon={TrendingDown}
          color={summary?.benefits_below_target > 0 ? 'red' : 'green'}
          testId="metric-below-target"
        />
        <MetricCard
          title="Exceeded Target"
          value={summary?.benefits_exceeded || 0}
          subtitle="Overperforming"
          icon={TrendingUp}
          color="green"
          testId="metric-exceeded"
        />
        <MetricCard
          title="Overall Attainment"
          value={formatPercent(summary?.overall_attainment)}
          subtitle="Realized / Expected"
          icon={Target}
          color={summary?.overall_attainment >= 80 ? 'green' : summary?.overall_attainment >= 60 ? 'amber' : 'red'}
          testId="metric-attainment"
        />
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Value by Category */}
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <PieChartIcon className="w-5 h-5 text-purple-500" />
            Value Distribution by Category
          </h3>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={true}
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => fmt(value)} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-400">
              No benefits data available
            </div>
          )}
        </div>
        
        {/* Category Attainment */}
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-500" />
            Attainment by Category
          </h3>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={categoryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                <Bar dataKey="attainment" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                  {categoryData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.attainment >= 80 ? '#10b981' : entry.attainment >= 60 ? '#f59e0b' : '#ef4444'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-400">
              No benefits data available
            </div>
          )}
        </div>
      </div>
      
      {/* At-Risk Benefits */}
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Benefits Requiring Executive Intervention
          </h3>
          <span className="text-sm text-slate-500">{atRiskBenefits?.length || 0} items</span>
        </div>
        {!atRiskBenefits || atRiskBenefits.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
            <p>No benefits at risk</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Benefit</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Expected</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Realized</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Variance</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Attainment</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Owner</th>
                </tr>
              </thead>
              <tbody>
                {atRiskBenefits.slice(0, 10).map((benefit, idx) => (
                  <tr 
                    key={benefit.benefit_id || idx}
                    className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                    onClick={() => onViewBenefit?.(benefit)}
                  >
                    <td className="py-3 px-4">
                      <p className="font-medium text-slate-900">{benefit.title}</p>
                      <p className="text-xs text-slate-500">{benefit.benefit_id}</p>
                    </td>
                    <td className="text-right py-3 px-4 font-medium text-slate-700">
                      {fmt(benefit.expected_value)}
                    </td>
                    <td className="text-right py-3 px-4 font-medium text-slate-700">
                      {fmt(benefit.realized_value)}
                    </td>
                    <td className={`text-right py-3 px-4 font-medium ${
                      (benefit.variance || benefit.variance_percent) < 0 ? 'text-red-600' : 'text-emerald-600'
                    }`}>
                      {formatPercent(benefit.variance_percent)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-center">
                        <AttainmentGauge value={benefit.attainment_percent || 0} size="small" />
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <StatusBadge status={benefit.status} />
                    </td>
                    <td className="py-3 px-4 text-slate-600 text-sm">
                      {benefit.owner_name || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Top Value Projects */}
      {topProjects && topProjects.length > 0 && (
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-indigo-500" />
            Top Value-Creating Projects
          </h3>
          <div className="space-y-3">
            {topProjects.slice(0, 5).map((project, idx) => (
              <div key={project.project_id || idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    idx === 0 ? 'bg-amber-100 text-amber-700' :
                    idx === 1 ? 'bg-slate-200 text-slate-600' :
                    idx === 2 ? 'bg-amber-50 text-amber-600' :
                    'bg-slate-100 text-slate-500'
                  }`}>
                    {idx + 1}
                  </span>
                  <div>
                    <p className="font-medium text-slate-900">Project {project.project_id?.slice(-6) || 'Unknown'}</p>
                    <p className="text-xs text-slate-500">{project.benefit_count} benefits</p>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-right">
                    <p className="text-slate-500">Expected</p>
                    <p className="font-semibold text-slate-700">{fmt(project.expected_value)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-500">Realized</p>
                    <p className="font-semibold text-emerald-600">{fmt(project.realized_value)}</p>
                  </div>
                  <div className="w-20">
                    <AttainmentGauge value={project.attainment_percent} size="small" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// PMO VIEW - Operational benefit tracking
// =============================================================================

const PMOView = ({ benefits, summary, onViewBenefit, currencySymbol = '$' }) => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('variance');
  
  // Create a currency formatter using the symbol
  const fmt = (val) => formatCurrencyStatic(val, currencySymbol);
  
  // Filter benefits
  const filteredBenefits = (benefits || []).filter(b => {
    if (filter === 'all') return true;
    if (filter === 'drifting') return b.status === 'at_risk' || b.status === 'below_target';
    if (filter === 'no_owner') return !b.owner_id;
    if (filter === 'delayed') {
      const targetDate = new Date(b.target_date);
      return targetDate < new Date() && b.lifecycle !== 'realized' && b.lifecycle !== 'closed';
    }
    if (filter === 'needs_measurement') return b.lifecycle === 'tracked' && !b.last_measured_at;
    return true;
  });
  
  // Sort benefits
  const sortedBenefits = [...filteredBenefits].sort((a, b) => {
    if (sortBy === 'variance') return (a.variance_percent || 0) - (b.variance_percent || 0);
    if (sortBy === 'attainment') return (a.attainment_percent || 0) - (b.attainment_percent || 0);
    if (sortBy === 'expected') return (b.expected_value || 0) - (a.expected_value || 0);
    if (sortBy === 'target_date') return new Date(a.target_date) - new Date(b.target_date);
    return 0;
  });
  
  // Counts for filter buttons
  const driftingCount = benefits?.filter(b => b.status === 'at_risk' || b.status === 'below_target').length || 0;
  const noOwnerCount = benefits?.filter(b => !b.owner_id).length || 0;
  const delayedCount = benefits?.filter(b => {
    const targetDate = new Date(b.target_date);
    return targetDate < new Date() && b.lifecycle !== 'realized' && b.lifecycle !== 'closed';
  }).length || 0;
  
  return (
    <div className="space-y-6" data-testid="pmo-view">
      {/* PMO Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Benefits"
          value={summary?.benefits_count || 0}
          subtitle="Active benefits tracked"
          icon={Target}
          color="blue"
          testId="metric-total-benefits"
        />
        <MetricCard
          title="Drifting Benefits"
          value={driftingCount}
          subtitle="Below 80% attainment trajectory"
          icon={AlertTriangle}
          color={driftingCount > 0 ? 'amber' : 'green'}
          testId="metric-drifting"
        />
        <MetricCard
          title="No Owner Assigned"
          value={noOwnerCount}
          subtitle="Requires assignment"
          icon={Users}
          color={noOwnerCount > 0 ? 'red' : 'green'}
          testId="metric-no-owner"
        />
        <MetricCard
          title="Past Target Date"
          value={delayedCount}
          subtitle="Deadline exceeded"
          icon={Clock}
          color={delayedCount > 0 ? 'red' : 'green'}
          testId="metric-delayed"
        />
      </div>
      
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-slate-500">Filter:</span>
        {[
          { key: 'all', label: 'All Benefits', count: benefits?.length },
          { key: 'drifting', label: 'Drifting', count: driftingCount },
          { key: 'no_owner', label: 'No Owner', count: noOwnerCount },
          { key: 'delayed', label: 'Delayed', count: delayedCount }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === f.key 
                ? 'bg-slate-900 text-white' 
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {f.label} ({f.count || 0})
          </button>
        ))}
        
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-slate-500">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm"
          >
            <option value="variance">Variance (Worst First)</option>
            <option value="attainment">Attainment (Lowest First)</option>
            <option value="expected">Expected Value (Highest First)</option>
            <option value="target_date">Target Date (Earliest First)</option>
          </select>
        </div>
      </div>
      
      {/* Benefits Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Benefit</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Category</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Owner</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Expected</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Realized</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Attainment</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Lifecycle</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Target Date</th>
              </tr>
            </thead>
            <tbody>
              {sortedBenefits.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-slate-400">
                    <Target className="w-10 h-10 mx-auto mb-2" />
                    <p>No benefits match the filter</p>
                  </td>
                </tr>
              ) : (
                sortedBenefits.map((benefit) => {
                  const isPastDue = new Date(benefit.target_date) < new Date() && 
                    benefit.lifecycle !== 'realized' && benefit.lifecycle !== 'closed';
                  
                  return (
                    <tr 
                      key={benefit.benefit_id}
                      className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${
                        isPastDue ? 'bg-red-50/30' : ''
                      }`}
                      onClick={() => onViewBenefit?.(benefit)}
                    >
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{benefit.title}</p>
                        <p className="text-xs text-slate-500">{benefit.benefit_id}</p>
                      </td>
                      <td className="py-3 px-4">
                        <CategoryBadge category={benefit.category} />
                      </td>
                      <td className="py-3 px-4">
                        {benefit.owner_name ? (
                          <span className="text-sm text-slate-700">{benefit.owner_name}</span>
                        ) : (
                          <span className="text-sm text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            No owner
                          </span>
                        )}
                      </td>
                      <td className="text-right py-3 px-4 font-medium text-slate-700">
                        {fmt(benefit.expected_value)}
                      </td>
                      <td className="text-right py-3 px-4 font-medium text-slate-700">
                        {fmt(benefit.realized_value)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-center">
                          <AttainmentGauge value={benefit.attainment_percent || 0} size="small" />
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <LifecycleBadge lifecycle={benefit.lifecycle} />
                      </td>
                      <td className="py-3 px-4 text-center">
                        <StatusBadge status={benefit.status} />
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span className={isPastDue ? 'text-red-600 font-medium' : 'text-slate-600'}>
                          {benefit.target_date ? new Date(benefit.target_date).toLocaleDateString() : '-'}
                        </span>
                        {isPastDue && (
                          <span className="ml-1 text-red-600">⚠</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// GOVERNANCE VIEW - Approval and decision history
// =============================================================================

const GovernanceView = ({ benefits, decisions, onViewBenefit, currencySymbol = '$' }) => {
  // Create a currency formatter using the symbol
  const fmt = (val) => formatCurrencyStatic(val, currencySymbol);
  
  // Benefits that required approval (lifecycle transitions)
  const approvedBenefits = (benefits || []).filter(b => b.approved_at);
  const realizedBenefits = (benefits || []).filter(b => b.lifecycle === 'realized' || b.lifecycle === 'closed');
  const significantChanges = (benefits || []).filter(b => 
    b.history?.some(h => h.action === 'updated' && h.changes?.expected_value)
  );
  
  // Benefits that triggered governance events (at_risk or below_target)
  const governanceTriggered = (benefits || []).filter(b => 
    b.status === 'at_risk' || b.status === 'below_target'
  );
  
  return (
    <div className="space-y-6" data-testid="governance-view">
      {/* Governance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Benefits Approved"
          value={approvedBenefits.length}
          subtitle="Passed governance review"
          icon={CheckCircle}
          color="green"
          testId="metric-approved"
        />
        <MetricCard
          title="Benefits Realized"
          value={realizedBenefits.length}
          subtitle="Value confirmed"
          icon={Award}
          color="purple"
          testId="metric-realized"
        />
        <MetricCard
          title="Significant Changes"
          value={significantChanges.length}
          subtitle="Expected value modified"
          icon={FileText}
          color="blue"
          testId="metric-changes"
        />
        <MetricCard
          title="Governance Triggered"
          value={governanceTriggered.length}
          subtitle="At-risk or below target"
          icon={AlertTriangle}
          color={governanceTriggered.length > 0 ? 'amber' : 'green'}
          testId="metric-governance-triggered"
        />
      </div>
      
      {/* Recent Approvals */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-emerald-500" />
          Recent Benefit Approvals
        </h3>
        {approvedBenefits.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <FileText className="w-10 h-10 mx-auto mb-2" />
            <p>No benefit approvals yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {approvedBenefits.slice(0, 5).map((benefit) => (
              <div 
                key={benefit.benefit_id}
                className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                onClick={() => onViewBenefit?.(benefit)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{benefit.title}</p>
                    <p className="text-sm text-slate-500">
                      Approved by {benefit.approved_by_name || 'Unknown'} • {
                        benefit.approved_at ? new Date(benefit.approved_at).toLocaleDateString() : '-'
                      }
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-slate-700">{fmt(benefit.expected_value)}</p>
                  <p className="text-xs text-slate-500">Expected Value</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Governance Events */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Benefits Triggering Governance Review
        </h3>
        {governanceTriggered.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
            <p>No benefits currently triggering governance events</p>
          </div>
        ) : (
          <div className="space-y-3">
            {governanceTriggered.map((benefit) => (
              <div 
                key={benefit.benefit_id}
                className={`p-4 rounded-lg cursor-pointer transition-colors ${
                  benefit.status === 'below_target' 
                    ? 'bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500' 
                    : 'bg-amber-50 hover:bg-amber-100 border-l-4 border-l-amber-500'
                }`}
                onClick={() => onViewBenefit?.(benefit)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <StatusBadge status={benefit.status} />
                      <span className="text-sm text-slate-500">
                        Attainment: {formatPercent(benefit.attainment_percent)}
                      </span>
                    </div>
                    <p className="font-medium text-slate-900">{benefit.title}</p>
                    <p className="text-sm text-slate-500">
                      Owner: {benefit.owner_name || 'Unassigned'} • 
                      Variance: {formatPercent(benefit.variance_percent)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-500 text-sm">Value Gap</p>
                    <p className="text-xl font-bold text-red-600">
                      {fmt(Math.abs(benefit.variance || 0))}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Decision Trail */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-500" />
          Benefit Decision Trail
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          Every benefit lifecycle change is recorded in the Decision Ledger for full audit traceability.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Decision Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Benefit</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Made By</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Rationale</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {(!decisions || decisions.length === 0) ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-slate-400">
                    <FileText className="w-8 h-8 mx-auto mb-2" />
                    <p>No benefit decisions recorded yet</p>
                  </td>
                </tr>
              ) : (
                decisions.slice(0, 10).map((decision, idx) => (
                  <tr key={decision.decision_id || idx} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        decision.decision_type?.includes('approved') ? 'bg-emerald-100 text-emerald-700' :
                        decision.decision_type?.includes('at_risk') ? 'bg-amber-100 text-amber-700' :
                        decision.decision_type?.includes('realized') ? 'bg-purple-100 text-purple-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {decision.decision_type?.replace('benefit_', '').replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">{decision.entity_name || '-'}</td>
                    <td className="py-3 px-4 text-sm text-slate-700">{decision.made_by_name || '-'}</td>
                    <td className="py-3 px-4 text-sm text-slate-500 max-w-xs truncate">{decision.rationale || '-'}</td>
                    <td className="py-3 px-4 text-sm text-slate-500">
                      {decision.timestamp ? new Date(decision.timestamp).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN DASHBOARD COMPONENT
// =============================================================================

const BenefitsDashboard = () => {
  const navigate = useNavigate();
  const { formatCurrency, currencySymbol } = useCurrency();
  const [activeView, setActiveView] = useState('executive');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data state
  const [summary, setSummary] = useState(null);
  const [outcomeScore, setOutcomeScore] = useState(null);
  const [atRiskBenefits, setAtRiskBenefits] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [allBenefits, setAllBenefits] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [topProjects, setTopProjects] = useState([]);
  
  // Fetch all data
  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const headers = getAuthHeaders();
      
      // Fetch all data in parallel
      const [summaryRes, scoreRes, atRiskRes, categoryRes, benefitsRes, dashboardRes] = await Promise.all([
        axios.get(`${API_BASE}/benefits/summary`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_BASE}/benefits/outcome-score`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_BASE}/benefits/at-risk`, { headers }).catch(() => ({ data: { benefits: [] } })),
        axios.get(`${API_BASE}/benefits/by-category`, { headers }).catch(() => ({ data: { by_category: {} } })),
        axios.get(`${API_BASE}/benefits?page_size=100`, { headers }).catch(() => ({ data: { benefits: [] } })),
        axios.get(`${API_BASE}/benefits/dashboard/executive`, { headers }).catch(() => ({ data: { dashboard: {} } }))
      ]);
      
      setSummary(summaryRes.data?.summary || {});
      setOutcomeScore(scoreRes.data?.outcome_score || {});
      setAtRiskBenefits(atRiskRes.data?.benefits || []);
      setByCategory(categoryRes.data?.by_category || {});
      setAllBenefits(benefitsRes.data?.benefits || []);
      
      // Extract top projects from dashboard response
      if (dashboardRes.data?.dashboard) {
        setTopProjects(dashboardRes.data.dashboard.top_value_projects || []);
      }
      
      // Fetch benefit decisions from decision ledger
      // Note: The decision-ledger endpoint requires tenant_id as a query param for impersonation
      try {
        const tenantId = localStorage.getItem('impersonated_tenant_id') || 
                         localStorage.getItem('simpliview_user_tenant_id') ||
                         localStorage.getItem('simpliview_tenant_id');
        const decisionsRes = await axios.get(
          `${API_BASE}/decision-ledger?category=benefits_management&limit=20${tenantId ? `&tenant_id=${tenantId}` : ''}`, 
          { headers }
        );
        setDecisions(decisionsRes.data?.decisions || []);
      } catch {
        // Fallback if decision ledger fails
        setDecisions([]);
      }
      
    } catch (error) {
      console.error('Failed to fetch benefits data:', error);
      toast.error('Failed to load benefits data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleRefresh = () => fetchData(true);
  
  const handleViewBenefit = (benefit) => {
    // Could navigate to detail view or open modal
    toast.info(`Viewing benefit: ${benefit.title || benefit.benefit_id}`);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-slate-400 mx-auto" />
          <p className="mt-4 text-slate-500">Loading Outcome Intelligence...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="benefits-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                Benefits Dashboard
              </h1>
              <p className="text-slate-500 mt-1">
                Outcome Intelligence Layer • Did the investment create the value we approved?
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-5 h-5 text-slate-600 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => navigate('/demands/new')}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
                data-testid="new-benefit-btn"
              >
                <Plus className="w-4 h-4" />
                New Benefit
              </button>
            </div>
          </div>
          
          {/* View Tabs */}
          <div className="flex gap-3 mt-6">
            <ViewTab 
              active={activeView === 'executive'} 
              label="Executive View" 
              icon={Award}
              onClick={() => setActiveView('executive')}
              testId="tab-executive"
            />
            <ViewTab 
              active={activeView === 'pmo'} 
              label="PMO View" 
              icon={Users}
              count={summary?.benefits_count}
              onClick={() => setActiveView('pmo')}
              testId="tab-pmo"
            />
            <ViewTab 
              active={activeView === 'governance'} 
              label="Governance View" 
              icon={Scale}
              count={atRiskBenefits?.length || 0}
              onClick={() => setActiveView('governance')}
              testId="tab-governance"
            />
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeView === 'executive' && (
          <ExecutiveView 
            summary={summary}
            outcomeScore={outcomeScore}
            atRiskBenefits={atRiskBenefits}
            byCategory={byCategory}
            topProjects={topProjects}
            onViewBenefit={handleViewBenefit}
            currencySymbol={currencySymbol}
          />
        )}
        {activeView === 'pmo' && (
          <PMOView 
            benefits={allBenefits}
            summary={summary}
            onViewBenefit={handleViewBenefit}
            currencySymbol={currencySymbol}
          />
        )}
        {activeView === 'governance' && (
          <GovernanceView 
            benefits={allBenefits}
            decisions={decisions}
            onViewBenefit={handleViewBenefit}
            currencySymbol={currencySymbol}
          />
        )}
      </div>
    </div>
  );
};

export default BenefitsDashboard;
